/**
 * Compiled SpriteDef timeline: per-frame display-list state used for timeline playback and gotos.
 *
 * State of frame f = state of f-1 + frame f's ops (all RemoveObject first, then PlaceObject in tag
 * order, like AS3 playback). Each depth holds a "lifetime" (one character instance placed at
 * `startFrame`); every PlaceObject field remembers the frame it was last set in, so a forward goto
 * f -> t only re-applies fields changed in (f, t] (Ruffle's merged goto commands).
 */
import type { ColorTransform8, FilterDef, FrameOp, FrameScriptOp, Matrix6, PlaceOp, SpriteDef } from '../../assets/manifest';

export interface DepthState {
  charId: number;
  startFrame: number;
  name?: string;
  className?: string;
  clipDepth?: number;
  matrix?: Matrix6;
  colorTransform?: ColorTransform8;
  ratio?: number;
  filters?: FilterDef[];
  blendMode?: string;
  visible?: boolean;
  cacheAsBitmap?: boolean;
  /** Frame numbers at which each field / the character was last set. */
  set: { matrix?: number; colorTransform?: number; ratio?: number; filters?: number; blendMode?: number; visible?: number; cacheAsBitmap?: number; charId?: number };
}

export interface FrameLabelInfo {
  name: string;
  frame: number;
}

const PLACE_FIELDS = ['matrix', 'colorTransform', 'ratio', 'filters', 'blendMode', 'visible', 'cacheAsBitmap'] as const;

function applyFields(s: DepthState, op: PlaceOp, frame: number): void {
  for (const k of PLACE_FIELDS) {
    const v = (op as any)[k];
    if (v !== undefined) {
      (s as any)[k] = v;
      s.set[k] = frame;
    }
  }
}

export class CompiledTimeline {
  readonly def: SpriteDef;
  readonly totalFrames: number;
  readonly labels: FrameLabelInfo[];
  readonly labelMap: Map<string, number>;
  private readonly states: Array<Map<number, DepthState> | undefined> = [];

  constructor(def: SpriteDef) {
    this.def = def;
    this.totalFrames = Math.max(1, def.frameCount | 0 || def.frames.length || 1);
    this.labels = Object.entries(def.labels ?? {})
      .map(([name, frame]) => ({ name, frame: frame | 0 }))
      .sort((a, b) => a.frame - b.frame);
    this.labelMap = new Map(this.labels.map((l) => [l.name, l.frame]));
  }

  opsAt(frame: number): FrameOp[] {
    return this.def.frames[frame - 1] ?? [];
  }

  scriptsAt(frame: number): FrameScriptOp[] | undefined {
    const s = this.def.scripts?.[frame];
    return s && s.length ? s : undefined;
  }

  /** Display-list state at `frame` (1-based). */
  stateAt(frame: number): Map<number, DepthState> {
    frame = Math.max(1, Math.min(frame, this.totalFrames));
    const cached = this.states[frame];
    if (cached) return cached;
    let start = frame;
    while (start > 1 && !this.states[start - 1]) start--;
    let prev: Map<number, DepthState> = start > 1 ? (this.states[start - 1] as Map<number, DepthState>) : new Map();
    for (let f = start; f <= frame; f++) {
      const next = new Map(prev);
      const ops = this.opsAt(f);
      for (const op of ops) if (op.op === 'remove') next.delete(op.depth);
      for (const op of ops) {
        if (op.op !== 'place') continue;
        const cur = next.get(op.depth);
        if (op.charId !== undefined && (!op.move || !cur)) {
          if (cur && !op.move) continue; // Flash fails to place over an occupied depth
          const s: DepthState = { charId: op.charId, startFrame: f, set: { charId: f } };
          if (op.name !== undefined) s.name = op.name;
          if (op.className !== undefined) s.className = op.className;
          if (op.clipDepth !== undefined) s.clipDepth = op.clipDepth;
          applyFields(s, op, f);
          next.set(op.depth, s);
        } else if (cur) {
          const s: DepthState = { ...cur, set: { ...cur.set } };
          if (op.charId !== undefined && op.charId !== cur.charId) {
            s.charId = op.charId;
            s.set.charId = f;
          }
          applyFields(s, op, f);
          next.set(op.depth, s);
        }
      }
      this.states[f] = next;
      prev = next;
    }
    return this.states[frame] as Map<number, DepthState>;
  }
}

const cache = new WeakMap<SpriteDef, CompiledTimeline>();

export function compileTimeline(def: SpriteDef): CompiledTimeline {
  let tl = cache.get(def);
  if (!tl) {
    tl = new CompiledTimeline(def);
    cache.set(def, tl);
  }
  return tl;
}

/** PlaceObject properties of a depth state, optionally only those changed after `afterFrame`. */
export function placeProps(s: DepthState, afterFrame: number = 0): {
  matrix?: number[];
  colorTransform?: number[];
  ratio?: number;
  filters?: FilterDef[];
  blendMode?: string;
  visible?: boolean;
  cacheAsBitmap?: boolean;
} {
  const out: any = {};
  for (const k of PLACE_FIELDS) {
    const f = s.set[k];
    if (f !== undefined && f > afterFrame) out[k] = (s as any)[k];
  }
  return out;
}
